import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { MapPin, TrendingUp, ArrowRight } from "lucide-react";
import { SeoHead } from "@/lib/seo";
import { Breadcrumb, breadcrumbJsonLd } from "@/components/Breadcrumb";
import { TOWN_COORDS } from "@/lib/salento-coords";
import { useLang } from "@/lib/lang-context";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SALENTO_CENTER = [40.35, 17.95];

/**
 * Tier → color mapping. Five buckets keep the legend readable and the
 * choropleth feel intact. Thresholds aligned to typical OMI quotations
 * across Salento (lowest ~700€/m², highest ~3000€/m²).
 */
const TIERS = [
  { max: 1000, color: "#4A5D23", label: "≤ 1.000" },
  { max: 1400, color: "#7A8A4A", label: "1.000–1.400" },
  { max: 1800, color: "#C9B96A", label: "1.400–1.800" },
  { max: 2300, color: "#C97A3A", label: "1.800–2.300" },
  { max: Infinity, color: "#8B2E1F", label: "> 2.300" },
];

function colorFor(value) {
  for (const t of TIERS) {
    if (value <= t.max) return t.color;
  }
  return "#6C706D";
}

function radiusFor(value) {
  // 1000 → 8px, 3000 → 22px. Linear scale, capped.
  const r = 6 + Math.min(28, (value - 700) / 110);
  return Math.max(8, Math.min(28, r));
}

const OmiHeatMapPage = () => {
  const { lang } = useLang();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [towns, setTowns] = useState([]);
  const [hovered, setHovered] = useState(null);

  // Pull the canonical OMI table from the backend, then join with hardcoded
  // coordinates for towns we know the lat/lng of.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/omi`);
        const enriched = (data || [])
          .map((t) => {
            const key = t.town.toLowerCase();
            const coords = TOWN_COORDS[key];
            return coords ? { ...t, lat: coords[0], lng: coords[1] } : null;
          })
          .filter(Boolean);
        setTowns(enriched);
      } catch (e) {
        // Non-fatal; page degrades to map without points.
        // eslint-disable-next-line no-console
        console.error("[omi-map] fetch failed", e);
      }
    })();
  }, []);

  // Mount the Leaflet map ONCE.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: SALENTO_CENTER,
      zoom: 9,
      scrollWheelZoom: false,
      attributionControl: true,
    });
    L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
        maxZoom: 18,
      }
    ).addTo(map);
    mapRef.current = map;
  }, []);

  // Render circle markers whenever the towns dataset changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || towns.length === 0) return;
    const layer = L.layerGroup().addTo(map);
    towns.forEach((t) => {
      const v = t.residential_eur_sqm;
      const circle = L.circleMarker([t.lat, t.lng], {
        radius: radiusFor(v),
        color: "#282C2A",
        weight: 1,
        fillColor: colorFor(v),
        fillOpacity: 0.78,
      });
      const popup = `
        <div style="font-family:'Manrope',sans-serif;min-width:180px;">
          <div style="font-family:'Cormorant Garamond',serif;font-size:20px;color:#282C2A;line-height:1.15;">
            ${t.town}
          </div>
          <div style="font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:#6C706D;margin-top:4px;">
            Prov. ${t.province}
          </div>
          <div style="margin-top:10px;font-family:'JetBrains Mono',monospace;font-size:13px;color:#282C2A;">
            <strong>€ ${v.toLocaleString("it-IT")}</strong> / m²
            <span style="color:#6C706D;font-size:10px;"> residenziale OMI</span>
          </div>
          ${
            t.agricultural_eur_sqm
              ? `<div style="font-family:'JetBrains Mono',monospace;font-size:11px;color:#6C706D;">
                  € ${t.agricultural_eur_sqm.toLocaleString("it-IT")} / m² terreno agricolo
                </div>`
              : ""
          }
        </div>`;
      circle.bindPopup(popup, { maxWidth: 240 });
      circle.on("mouseover", () => setHovered(t));
      circle.on("mouseout", () => setHovered(null));
      circle.addTo(layer);
    });
    return () => {
      layer.remove();
    };
  }, [towns]);

  // Cheapest / most expensive callouts for the editorial sidebar.
  const sorted = [...towns].sort(
    (a, b) => a.residential_eur_sqm - b.residential_eur_sqm
  );
  const cheapest = sorted.slice(0, 5);
  const priciest = sorted.slice(-5).reverse();

  const seoTitle =
    lang === "it"
      ? "Mappa dei prezzi al m² · OMI Salento · Deal Rating"
      : "Price map · €/m² · OMI Salento · Deal Rating";
  const seoDesc =
    lang === "it"
      ? "Mappa interattiva dei prezzi residenziali al m² (dati OMI Agenzia Entrate) per oltre 50 comuni del Salento. Scopri dove vale ancora la pena comprare casa."
      : "Interactive heat map of residential €/m² prices (OMI Agenzia Entrate data) across 50+ Salento towns. See where Apulian real-estate still offers value.";

  return (
    <main className="bg-[#FBFAF7] min-h-screen">
      <SeoHead
        title={seoTitle}
        description={seoDesc}
        canonical="/mappa-prezzi-salento"
        jsonLd={[
          breadcrumbJsonLd([
            { label: "Home", to: "/" },
            { label: "Mappa prezzi Salento", to: "/mappa-prezzi-salento" },
          ]),
        ]}
      />

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <Breadcrumb
          items={[
            { label: lang === "it" ? "Home" : "Home", to: "/" },
            {
              label: lang === "it" ? "Mappa prezzi" : "Price map",
              to: "/mappa-prezzi-salento",
            },
          ]}
        />

        <div className="mt-6 mb-8">
          <div className="label-eyebrow inline-flex items-center gap-2">
            <MapPin size={11} strokeWidth={1.75} />
            {lang === "it" ? "Dati OMI · Agenzia Entrate" : "OMI · Italian Revenue Agency"}
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#282C2A] leading-tight mt-3">
            {lang === "it"
              ? "Mappa dei prezzi: dove costa di più la casa in Salento."
              : "Price map: where does Salento real-estate cost the most."}
          </h1>
          <p className="mt-4 text-base text-[#6C706D] max-w-3xl leading-relaxed">
            {lang === "it"
              ? "Ogni punto è un comune del Salento, colorato in base al prezzo medio residenziale al m² (€/m²) secondo le quotazioni OMI dell'Agenzia delle Entrate. Più scuro il rosso, più caro; più scuro il verde, più conveniente."
              : "Each dot is a Salento town, coloured by the OMI residential €/m² benchmark. Darker red = pricier; darker green = better value."}
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_280px] gap-6">
          {/* Map */}
          <div className="relative">
            <div
              data-testid="omi-heat-map"
              ref={containerRef}
              className="w-full h-[480px] md:h-[620px] border-2 border-[#282C2A] shadow-paper bg-[#F0EFEB]"
            />
            {hovered && (
              <div className="absolute top-3 left-3 bg-white border border-[#282C2A] px-4 py-2 shadow-paper text-xs pointer-events-none">
                <div className="font-serif-display text-lg text-[#282C2A] leading-none">
                  {hovered.town}
                </div>
                <div className="font-mono text-[#4A5D23] mt-1">
                  € {hovered.residential_eur_sqm.toLocaleString("it-IT")} / m²
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <div className="bg-white border-2 border-[#282C2A] shadow-paper p-5">
              <div className="label-eyebrow text-[#6C706D]">
                {lang === "it" ? "Legenda · €/m²" : "Legend · €/sqm"}
              </div>
              <ul className="mt-3 space-y-2">
                {TIERS.map((t, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm">
                    <span
                      className="inline-block w-4 h-4 border border-[#282C2A]"
                      style={{ backgroundColor: t.color }}
                    />
                    <span className="font-mono text-[#282C2A]">€ {t.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white border-2 border-[#282C2A] shadow-paper p-5">
              <div className="label-eyebrow text-[#4A5D23] flex items-center gap-2">
                <TrendingUp size={11} strokeWidth={1.75} />
                {lang === "it" ? "Più convenienti" : "Best value"}
              </div>
              <ul className="mt-3 space-y-1 text-sm">
                {cheapest.map((t) => (
                  <li key={t.town} className="flex justify-between font-mono text-[#282C2A]">
                    <span className="truncate">{t.town}</span>
                    <span className="text-[#4A5D23]">
                      € {t.residential_eur_sqm.toLocaleString("it-IT")}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white border-2 border-[#8B2E1F] shadow-paper p-5">
              <div className="label-eyebrow text-[#8B2E1F]">
                {lang === "it" ? "Più cari" : "Most expensive"}
              </div>
              <ul className="mt-3 space-y-1 text-sm">
                {priciest.map((t) => (
                  <li key={t.town} className="flex justify-between font-mono text-[#282C2A]">
                    <span className="truncate">{t.town}</span>
                    <span className="text-[#8B2E1F]">
                      € {t.residential_eur_sqm.toLocaleString("it-IT")}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <Link
              to="/"
              data-testid="map-cta-analyze"
              className="block w-full text-center px-5 py-3 bg-[#4A5D23] text-white font-semibold tracking-wider uppercase text-xs border border-[#3B4A1C] hover:bg-[#3B4A1C] transition-colors shadow-[0_3px_0_#282C2A] hover:shadow-[0_2px_0_#282C2A] hover:translate-y-[1px]"
            >
              <span className="inline-flex items-center gap-2">
                {lang === "it" ? "Analizza un annuncio" : "Analyse a listing"}
                <ArrowRight size={14} strokeWidth={1.75} />
              </span>
            </Link>
          </aside>
        </div>

        <p className="mt-10 text-xs text-[#6C706D] max-w-3xl leading-relaxed">
          {lang === "it"
            ? "Fonte: Osservatorio del Mercato Immobiliare (OMI) - Agenzia delle Entrate. I valori sono indicativi e si riferiscono al segmento residenziale ordinario; le quotazioni reali variano in funzione di stato di conservazione, posizione esatta e tipologia (centro storico, periferia, costa). Aggiornamento: 2025."
            : "Source: OMI - Italian Revenue Agency. Values are indicative for ordinary residential stock; actual quotations vary by condition, micro-location and category (historical centre vs periphery vs coastline). Updated 2025."}
        </p>
      </div>
    </main>
  );
};

export default OmiHeatMapPage;
